import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { metaForPath, SITE } from "../lib/routeMeta";

function setMeta(key, content, asProperty = false) {
  const attr = asProperty ? "property" : "name";
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!href) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("link");
    el.rel = "canonical";
    document.head.appendChild(el);
  }
  el.href = href;
}

/**
 * Keeps <head> correct across client-side navigation.
 *
 * The initial HTML is already right — scripts/seo-build.js writes a static
 * file per route — but a SPA navigation replaces the view without touching
 * the document head. This re-applies it, and also moves focus to <main> so a
 * screen-reader user is told the page changed rather than being left at the
 * bottom of the previous one.
 */
export default function RouteMeta() {
  const { pathname } = useLocation();

  useEffect(() => {
    const meta = metaForPath(pathname);
    document.title = meta.title;
    setMeta("description", meta.description);
    setMeta("robots", meta.robots);
    setMeta("og:title", meta.title, true);
    setMeta("og:description", meta.description, true);
    setMeta("twitter:title", meta.title);
    setMeta("twitter:description", meta.description);
    if (meta.canonical) setMeta("og:url", meta.canonical, true);
    setCanonical(meta.canonical);
  }, [pathname]);

  // Announce the navigation. Skipped on first paint, where the browser's own
  // page-load handling already does this and stealing focus would be wrong.
  useEffect(() => {
    if (window.__dcFirstPaintDone) {
      const main = document.getElementById("main");
      if (main) main.focus({ preventScroll: true });
    }
    window.__dcFirstPaintDone = true;
  }, [pathname]);

  return null;
}

export { SITE };
